import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { markLoggedIn, clearLoggedIn, isLoggedIn, setCsrfToken, clearCsrfToken } from '../lib/authState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Correctif B7 : plus aucun jeton en localStorage. Le cookie httpOnly pose par
// le backend porte la session ; il est invisible au JavaScript, donc inexploitable
// par une XSS. `withCredentials` est deja actif globalement (index.js), on le
// repete ici par securite au cas ou ce contexte serait monte isolement (tests).
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Conserve pour compatibilite avec les appelants existants : le cookie
  // suffit desormais, il n'y a plus d'en-tete d'authentification a construire.
  const getAuthHeaders = () => ({});

  const checkAuth = useCallback(async () => {
    // On n'a plus de jeton a inspecter : on interroge le serveur, qui lit le
    // cookie. Le drapeau local evite seulement un appel inutile au premier
    // chargement pour un visiteur jamais connecte.
    if (!isLoggedIn()) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      const currentUser = response.data?.user || response.data;
      setUser(currentUser);
      markLoggedIn();
      setCsrfToken(response.data?.csrf_token);
    } catch (error) {
      clearLoggedIn();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Ouvre la session a partir d'une reponse de connexion reussie (mot de
  // passe seul, ou mot de passe + code MFA) : les deux chemins posent le
  // meme cookie httpOnly cote serveur et doivent etre traites pareil ici.
  const _finalizeSession = (data) => {
    markLoggedIn();
    setCsrfToken(data?.csrf_token);
    const loggedUser = data?.user || data;
    setUser(loggedUser);
    return loggedUser;
  };

  const login = async (email, password) => {
    const response = await axios.post(
      `${API_URL}/api/auth/login`,
      {
        email: email.toLowerCase().trim(),
        password: password.trim(),
      }
    );

    // Double authentification (cahier des charges labo, §32) : mot de passe
    // correct mais session pas encore ouverte. Le backend renvoie un jeton
    // court a soumettre avec le code TOTP via verifyMfa() ci-dessous — on ne
    // marque surtout PAS l'utilisateur comme connecte a ce stade.
    if (response.data?.mfa_required) {
      return { mfaRequired: true, pendingToken: response.data.pending_token };
    }

    return _finalizeSession(response.data);
  };

  const verifyMfa = async (pendingToken, code) => {
    const response = await axios.post(
      `${API_URL}/api/auth/mfa/verify`,
      { pending_token: pendingToken, code: code.trim() }
    );
    return _finalizeSession(response.data);
  };

  const register = async (data) => {
    const response = await axios.post(
      `${API_URL}/api/auth/register`,
      data
    );
    return response.data;
  };

  const registerDoctor = async (data) => {
    const response = await axios.post(
      `${API_URL}/api/doctors/register`,
      data,
      { headers: getAuthHeaders() }
    );

    // Correctif : le backend pose un cookie de session valide des
    // l'inscription (comme /login), mais cet etat n'etait jamais reporte
    // cote client. isLoggedIn() restait a false : la toute premiere action
    // du medecin (sauvegarder son planning, ce qui le rend reservable par un
    // patient) echouait avec une erreur CSRF, et les notifications temps
    // reel (SSE) ne demarraient jamais — un rechargement ne corrigeait rien.
    markLoggedIn();
    setCsrfToken(response.data?.csrf_token);
    setUser(response.data);

    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(
        `${API_URL}/api/auth/logout`,
        {},
        { headers: getAuthHeaders() }
      );
    } catch (error) {
      console.error('Logout error:', error);
    }

    // Le cookie est supprime cote serveur par /api/auth/logout ; on nettoie
    // ici le drapeau local ainsi que d'eventuelles cles heritees.
    clearLoggedIn();
    clearCsrfToken();
    setUser(null);
  };

  const loginWithGoogle = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = encodeURIComponent(window.location.origin + '/auth/callback');
    const scope = encodeURIComponent('openid email profile');
    const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&access_type=offline`;
    window.location.href = url;
  };

  const handleGoogleCallback = async (code) => {
    const response = await axios.post(
      `${API_URL}/api/auth/google-callback`,
      { code, redirect_uri: window.location.origin + '/auth/callback' }
    );

    // Correctif S-TEL-GOOGLE : email inconnu cote serveur = creation de
    // compte. Aucune session n'est ouverte tant que le numero de telephone
    // n'a pas ete saisi ; on remonte la demande telle quelle a l'appelant
    // (AuthCallback.js), qui affiche le champ puis appelle
    // completeGoogleSignup ci-dessous.
    if (response.data?.needs_phone) {
      return response.data;
    }

    markLoggedIn();
    setCsrfToken(response.data?.csrf_token);

    const loggedUser = response.data?.user || response.data;

    setUser(loggedUser);
    return loggedUser;
  };

  // Correctif S-TEL-GOOGLE : deuxieme etape d'une inscription via Google.
  const completeGoogleSignup = async (signupToken, phone) => {
    const response = await axios.post(
      `${API_URL}/api/auth/google-complete`,
      { signup_token: signupToken, phone }
    );

    markLoggedIn();
    setCsrfToken(response.data?.csrf_token);

    const loggedUser = response.data?.user || response.data;

    setUser(loggedUser);
    return loggedUser;
  };

  const value = {
    user,
    loading,
    login,
    verifyMfa,
    register,
    registerDoctor,
    logout,
    loginWithGoogle,
    handleGoogleCallback,
    completeGoogleSignup,
    refreshUser: checkAuth,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isDoctor: user?.role === 'doctor',
    isPatient: user?.role === 'patient',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;